/**
 * Pure helpers over {@link HexTileMap} (queries, updates, iteration, validation).
 * Keep this module free of React, R3F, and three.
 */

import { axialHexDistance } from '../coords';
import { getHexTileMapKey } from './keys';
import type { HexTileCoordinates, HexTileMap, HexTileProperties, HexTileType } from './types';

/**
 * The six axial neighbour offsets in canonical pointy-top order starting East,
 * counter-clockwise: E, NE, NW, W, SW, SE.
 */
const AXIAL_NEIGHBOR_OFFSETS: readonly HexTileCoordinates[] = [
  { q: 1, r: 0 },
  { q: 1, r: -1 },
  { q: 0, r: -1 },
  { q: -1, r: 0 },
  { q: -1, r: 1 },
  { q: 0, r: 1 },
];

/**
 * Returns the six axial neighbours of `coordinates` in canonical pointy-top order
 * (East, NE, NW, West, SW, SE). Order is a stable documented contract.
 * Pure coordinate math — returned coords may lie outside any particular map.
 */
function axialNeighbors(coordinates: HexTileCoordinates): HexTileCoordinates[] {
  return AXIAL_NEIGHBOR_OFFSETS.map((offset) => ({
    q: coordinates.q + offset.q,
    r: coordinates.r + offset.r,
  }));
}

/**
 * Returns the tile at `coordinates` from `tiles`, or `undefined` if absent.
 */
function getTile(
  tiles: HexTileMap,
  coordinates: HexTileCoordinates
): HexTileProperties | undefined {
  return tiles.get(getHexTileMapKey(coordinates));
}

/**
 * Returns all hex coordinates within `radius` steps of `center` (inclusive disc).
 * Includes the center itself. Negative radius naturally yields [].
 * Deterministic order: outer bounding-box loop dq then dr, filtered by axial distance.
 */
function hexesWithinRadius(center: HexTileCoordinates, radius: number): HexTileCoordinates[] {
  const result: HexTileCoordinates[] = [];
  for (let dq = -radius; dq <= radius; dq++) {
    for (let dr = -radius; dr <= radius; dr++) {
      const candidate: HexTileCoordinates = { q: center.q + dq, r: center.r + dr };
      if (axialHexDistance(center, candidate) <= radius) {
        result.push(candidate);
      }
    }
  }
  return result;
}

/**
 * Returns true iff `tile` is defined and its type is in `terrainList`.
 */
function isPassable(
  tile: HexTileProperties | undefined,
  terrainList: readonly HexTileType[]
): boolean {
  return tile !== undefined && terrainList.includes(tile.type);
}

export { AXIAL_NEIGHBOR_OFFSETS, axialNeighbors, getTile, hexesWithinRadius, isPassable };
